import React, { useState, useEffect } from "react";
import { MainLayout } from "../layout/mainLayout";
import { PageTitle } from "../layout/pageTitle";
import { useTranslation } from "react-i18next";
import {
    IMuiAutocompleteOption,
    MuiAutocomplete,
} from "../framework/muiAutocomplete";
import { useAppDispatch, useAppSelector } from "../../framework/redux/hooks";
import {
    handleApiError,
    handleEasyOppApiError,
} from "../../services/baseService";
import { Box, Button, Card, Typography } from "@mui/material";
import { useAsyncThunkAction } from "../../hooks/framework/useAsyncThunkAction";
import { LoadingIndicatorInline } from "../framework/loadingIndicator";
import { fetchCostCenters, fetchEmployeeCostCenters, setEmployeeCostCenters } from "../../framework/redux/slices/costCenterSlice";
import { ICostCenterListItem } from "../../models/costCenter/costCenterListItem";
import { updateEmployeeCostCenters } from "../../services/costCenterService";

export const EmployeeCostCenterSelect = () => {
    useAsyncThunkAction(() => fetchCostCenters(), { onError: handleApiError }, []);
    useAsyncThunkAction(
        () => fetchEmployeeCostCenters(),
        { onError: handleApiError },
        []
    );

    const costCenters = useAppSelector((state) => state.costCenter.costCenterItems);
    const employeeCostCenters = useAppSelector((state) => state.costCenter.employeeCostCenterItems);
    const dispatch = useAppDispatch();
    const hasEmployeeCostCenters = useAppSelector(
        (state) => state.costCenter.hasEmployeeCostCenters
    );
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState<boolean>(true);

    const convertCostCenters = (
        costCenter: ICostCenterListItem
    ): IMuiAutocompleteOption => ({
        id: costCenter.id,
        title: `${costCenter?.name}`,
    });

    const [selectedOptions, setSelectedOptions] = useState<
        IMuiAutocompleteOption[]
    >([]);

    useEffect(() => {
        if (hasEmployeeCostCenters) {
            setSelectedOptions(employeeCostCenters.map(convertCostCenters));
        } else {
            setSelectedOptions([]);
        }
        setIsLoading(false);
    }, [employeeCostCenters]);

    const handleCostCenterChange = (
        selectedCostCenters: IMuiAutocompleteOption[]
    ) => {
        setSelectedOptions(selectedCostCenters);
        const selected = costCenters.filter(c => selectedCostCenters.some(sc => sc.id === c.id));
        const hasEmployeeCostCenters = selectedCostCenters.length > 0;
        setEmployeeCostCenters({ costCenters: selected, hasEmployeeCostCenters });
    };

    const handleClick = async() => {
        const SaveEmployeeCostCenters = {
            CostCenterIds: selectedOptions.map((option) => option.id),
        };
        try {
            await updateEmployeeCostCenters(SaveEmployeeCostCenters, null);
            await dispatch(fetchEmployeeCostCenters());
        } catch (e) {
            handleEasyOppApiError(e);
        }
    };

    if (isLoading)
        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="100vh"
            >
                <LoadingIndicatorInline />
            </Box>
        );

    return (
        <MainLayout
            topComponent={
                <PageTitle header={t("settings.costCenters.costCenterSelection")} />
            }
            hasPx
            bottomComponent={
                hasEmployeeCostCenters ? (
                    <Card sx={{ bgcolor: "lightBlue.main", padding: 2 }}>
                        <Typography variant="h4" marginBottom={2}>
                            {`${t("settings.costCenters.myCostCenters")} (${
                                employeeCostCenters.length
                            } ${t("common.measureUnitPcs")})`}
                        </Typography>
                        {employeeCostCenters.map((c) => (
                            <Typography variant="subtitle2" key={c.id}>
                                {c?.name}
                            </Typography>
                        ))}
                    </Card>
                ) : (
                    <Typography variant="h4">
                        {t("settings.costCenters.noCostCentersSpecified")}
                    </Typography>
                )
            }
        >
            <MuiAutocomplete
                options={costCenters?.length > 0 ? costCenters.map(c => { return { id: c?.id, title: c?.name }; }) : []}
                label={t("settings.costCenters.costCenter")}
                onChange={handleCostCenterChange}
                value={selectedOptions}
                helperText={t("settings.costCenters.pickOneOrMore")}
            />
            <Box sx={{ my: 2 }}>
                <Button onClick={() => void handleClick()} variant="contained">
                    {t("settings.costCenters.saveSelections")}
                </Button>
            </Box>
        </MainLayout>
    );
};
